import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Navigation } from '@/components/ui/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Check, Star } from 'lucide-react';
import { useEffect } from 'react';

const SystemDetail = () => {
  const { code } = useParams();

  const { data: system, isLoading } = useQuery({
    queryKey: ['system', code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('systems')
        .select(`
          *,
          modules:modules(*)
        `)
        .eq('code', code)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  // Track page view
  useEffect(() => {
    if (system && typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'system_card_view',
        system: system.code,
      });
    }
  }, [system]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container max-w-screen-xl py-section">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="h-12 bg-muted rounded w-96"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!system) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container max-w-screen-xl py-section">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Sistema não encontrado</h1>
            <Button asChild>
              <Link to="/">Voltar ao início</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const getModuleTypeColor = (type: string) => {
    switch (type) {
      case 'core':
        return 'default';
      case 'premium':
        return 'secondary';
      case 'enterprise':
        return 'outline';
      default:
        return 'default';
    }
  };

  const getModuleTypeLabel = (type: string) => {
    switch (type) {
      case 'core':
        return 'Essencial';
      case 'premium':
        return 'Premium';
      case 'enterprise':
        return 'Enterprise';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="container max-w-screen-xl py-section">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/" className="flex items-center space-x-2">
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar ao catálogo</span>
            </Link>
          </Button>
        </div>

        {/* Hero Section */}
        <div className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold tracking-tight mb-6">
                {system.name}
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                {system.description}
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Button size="lg" asChild>
                  <Link to={`/signup?system=${system.code}`}>
                    Começar teste grátis
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/contact">
                    Agendar demonstração
                  </Link>
                </Button>
              </div>

              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Check className="h-4 w-4 text-success" />
                  <span>14 dias grátis</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Check className="h-4 w-4 text-success" />
                  <span>Sem cartão de crédito</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Check className="h-4 w-4 text-success" />
                  <span>Suporte incluído</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-gradient-card shadow-hero">
                <CardContent className="p-8">
                  <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4 mx-auto">
                        <span className="text-2xl font-bold text-primary-foreground">
                          {system.name.charAt(0)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Interface do {system.name}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Modules Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight mb-4">
              Módulos disponíveis
            </h2>
            <p className="text-xl text-muted-foreground">
              Ative apenas os módulos que sua empresa precisa
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {system.modules?.map((module: any) => (
              <Card key={module.id} className="relative">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg">{module.name}</CardTitle>
                    <Badge variant={getModuleTypeColor(module.type) as any}>
                      {getModuleTypeLabel(module.type)}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Código: {module.code}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant="outline" className="text-xs">
                        Disponível
                      </Badge>
                    </div>
                    
                    {module.type === 'core' && (
                      <div className="flex items-center space-x-2 text-sm text-success">
                        <Check className="h-4 w-4" />
                        <span>Incluído no plano básico</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Pricing Preview */}
        <div className="mb-16">
          <Card className="bg-gradient-feature">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">
                Comece agora mesmo
              </h3>
              <p className="text-muted-foreground mb-6">
                Teste todos os módulos por 14 dias, sem compromisso
              </p>
              
              <div className="flex justify-center items-center space-x-4 mb-6">
                <div className="flex items-center space-x-1">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="h-5 w-5 fill-warning text-warning" />
                  ))}
                </div>
                <div className="text-sm text-muted-foreground">
                  4.9/5 - Mais de 500 empresas confiam
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <Link to={`/signup?system=${system.code}`}>
                    Começar teste grátis
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/contact">
                    Falar com especialista
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4">
            Precisa de mais informações?
          </h3>
          <p className="text-muted-foreground mb-6">
            Nossa equipe está pronta para responder suas dúvidas
          </p>
          <Button variant="outline" asChild>
            <Link to="/contact">
              Entre em contato
            </Link>
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SystemDetail;